/**
 * ProdCatEditor — 공유 제품 카탈로그 에디터
 *
 * 새 정규화 API 사용:
 *  READ   GET  /api/prodcat/groups?slug=X  → TB_PROD_GROUP + TB_PROD_ITEM
 *  WRITE  POST /api/prodcat/group          → 그룹(대분류) 생성·수정
 *  WRITE  POST /api/prodcat/item           → 아이템 생성·수정
 *  DEL    DELETE /api/prodcat/group/{id}   → 그룹 삭제(아이템 cascade)
 *  DEL    DELETE /api/prodcat/item/{id}    → 아이템 단건 삭제
 *
 * Props:
 *  slug          (string)   solSlug (ipc, na, industrial, railway, sensor, …)
 *  title         (string)   에디터 제목
 *  domainSection (element)  도메인 헤더 SectionBuilder (옵션)
 *  onRegisterSave / onRegisterDirty / onBack
 *  refreshKey
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { authFetch } from '../../../auth';
import ImageDrop from '../../ImageDrop';
import { useDragReorder } from '../../useDragReorder';
import { SectionBuilder } from '../../components/SectionBuilder';

const API_GROUPS = '/api/prodcat/groups';
const API_GROUP  = '/api/prodcat/group';
const API_ITEM   = '/api/prodcat/item';

let _uid = 0;
const uid = () => `u${++_uid}`;

const toSlug = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
const newLink = () => ({ _id: uid(), key: '', url: '' });

function newItem() {
  return {
    _id: uid(), intNum: null,
    model: '', imageKeys: [], features: '', spec: '', tags: [], links: [],
  };
}
function newSubcat() { return { _id: uid(), subcat: '새 소분류', items: [newItem()] }; }
function newGroup()  { return { _id: uid(), intNum: null, category: '새 대분류', subcats: [newSubcat()] }; }

function parseItem(it) {
  return {
    _id: uid(),
    intNum: it.intNum ?? null,
    model: it.model || '',
    imageKeys: Array.isArray(it.imageKeys) ? it.imageKeys : [],
    features: Array.isArray(it.features) ? it.features.join('\n') : (it.features || ''),
    spec: it.spec || '',
    tags: Array.isArray(it.tags) ? it.tags : [],
    links: Array.isArray(it.links) ? it.links.map(l => ({ _id: uid(), key: l.key || '', url: l.url || '' })) : [],
  };
}

function parseGroup(g) {
  return {
    _id: uid(),
    intNum: g.intNum ?? null,
    category: g.catKey || '',
    vendor: g.vendor || '',
    subcats: (g.subcats || []).map(sc => ({
      _id: uid(),
      subcat: sc.subcat || '',
      items: (sc.items || []).map(parseItem),
    })),
  };
}

// ── 태그 입력 컴포넌트 ────────────────────────────────────────────────────────
function TagsEditor({ tags, onChange }) {
  const [input, setInput] = useState('');
  const addTag = () => {
    const v = input.trim();
    if (!v || tags.includes(v)) return;
    onChange([...tags, v]);
    setInput('');
  };
  const removeTag = (t) => onChange(tags.filter(x => x !== t));
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
        {tags.map(t => (
          <span key={t} style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            background: 'var(--color-accent,#2563eb)', color: '#fff',
            borderRadius: 4, padding: '2px 8px', fontSize: 12,
          }}>
            {t}
            <button type="button" onClick={() => removeTag(t)}
              style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 0, lineHeight: 1, fontSize: 14 }}>×</button>
          </span>
        ))}
        {tags.length === 0 && <span style={{ fontSize: 12, color: '#aaa' }}>태그 없음</span>}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          className="rp-admin-field-inp"
          style={{ flex: 1 }}
          value={input}
          placeholder="태그 입력 후 Enter 또는 추가"
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
        />
        <button type="button" className="rp-admin-add-btn" onClick={addTag}>추가</button>
      </div>
    </div>
  );
}

// ── 메인 컴포넌트 ─────────────────────────────────────────────────────────────
export default function ProdCatEditor({
  slug, title = '제품 관리', domainSection = null,
  domainSave = null, domainDirty = null,
  refreshKey = 0, onRegisterSave, onRegisterDirty, onBack,
}) {
  const [groups,    setGroups]    = useState(null);
  const [groupIdx,  setGroupIdx]  = useState(0);
  const [subcatIdx, setSubcatIdx] = useState(0);
  const [itemIdx,   setItemIdx]   = useState(0);

  const ref      = useRef([]);        // 현재 groups state (클로저 우회)
  const delItems = useRef([]);        // 삭제 예약된 item intNum 목록
  const delGroups = useRef([]);       // 삭제 예약된 group intNum 목록
  const dirtyRef = useRef(false);

  const filePick    = useRef(null);
  const filePending = useRef(null);
  const groupIdxRef = useRef(0);
  const [fileUploading, setFileUploading] = useState(false);

  const uploadFile = useCallback(async (file) => {
    if (!filePending.current) return;
    const apply = filePending.current;
    setFileUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await authFetch('/api/files', { method: 'POST', body: fd });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.message || `업로드 실패 (${res.status})`); }
      const f = await res.json();
      apply(f.url);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e.message || '파일 업로드 실패');
    } finally {
      setFileUploading(false);
      filePending.current = null;
    }
  }, []);

  groupIdxRef.current = groupIdx;

  // ── 로딩 ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    const prevIntNum = ref.current[groupIdxRef.current]?.intNum ?? null;
    authFetch(`${API_GROUPS}?slug=${slug}`).then(r => r.json()).then(data => {
      if (!alive) return;
      const parsed = (data || []).map(parseGroup);
      ref.current = parsed;
      setGroups(parsed);
      if (prevIntNum != null) {
        const ni = parsed.findIndex(g => g.intNum === prevIntNum);
        if (ni >= 0) setGroupIdx(ni);
      }
    }).catch(() => { if (alive) setGroups([]); });
    return () => { alive = false; };
  }, [slug, refreshKey]);

  useEffect(() => { setSubcatIdx(0); setItemIdx(0); }, [groupIdx]);
  useEffect(() => { setItemIdx(0); }, [subcatIdx]);

  const push = (next) => { dirtyRef.current = true; ref.current = next; setGroups([...next]); };

  // ── 그룹 CRUD ────────────────────────────────────────────────────────────
  const addGroup = () => {
    const n = newGroup();
    const next = [...ref.current, n];
    push(next); setGroupIdx(next.length - 1);
  };
  const delGroup = (gi) => {
    const g = ref.current[gi];
    if (!window.confirm('이 대분류와 모든 제품을 삭제할까요?')) return;
    if (g.intNum) delGroups.current.push(g.intNum);
    const next = ref.current.filter((_, i) => i !== gi);
    push(next);
    setGroupIdx(t => Math.min(t, Math.max(0, next.length - 1)));
  };
  const updGroup = (gi, val) => push(ref.current.map((g, i) => i === gi ? { ...g, category: val } : g));
  const reorderGroup = (from, to) => {
    const next = [...ref.current]; const [m] = next.splice(from, 1); next.splice(to, 0, m);
    push(next); setGroupIdx(to);
  };

  // ── 소분류 CRUD ──────────────────────────────────────────────────────────
  const addSubcat = (gi) => {
    const next = ref.current.map((g, i) => i === gi ? { ...g, subcats: [...g.subcats, newSubcat()] } : g);
    push(next); setSubcatIdx(next[gi].subcats.length - 1);
  };
  const delSubcat = (gi, si) => {
    // 소분류 삭제 시 해당 아이템들도 삭제 예약
    const items = ref.current[gi]?.subcats[si]?.items ?? [];
    items.forEach(it => { if (it.intNum) delItems.current.push(it.intNum); });
    push(ref.current.map((g, i) => i === gi ? { ...g, subcats: g.subcats.filter((_, j) => j !== si) } : g));
    setSubcatIdx(idx => Math.max(0, Math.min(idx, (ref.current[gi]?.subcats?.length ?? 1) - 1)));
  };
  const updSubcat = (gi, si, val) => push(ref.current.map((g, i) =>
    i === gi ? { ...g, subcats: g.subcats.map((sc, j) => j === si ? { ...sc, subcat: val } : sc) } : g));
  const reorderSubcat = useCallback((from, to) => {
    const gi = groupIdxRef.current;
    const next = ref.current.map((g, i) => {
      if (i !== gi) return g;
      const subs = [...g.subcats]; const [m] = subs.splice(from, 1); subs.splice(to, 0, m);
      return { ...g, subcats: subs };
    });
    push(next); setSubcatIdx(to);
  }, []); // eslint-disable-line

  const reorderItem = useCallback((from, to) => {
    const gi = groupIdxRef.current;
    push(ref.current.map((g, i) => {
      if (i !== gi) return g;
      return {
        ...g, subcats: g.subcats.map((sc, j) => {
          if (j !== subcatIdx) return sc;
          const its = [...sc.items]; const [m] = its.splice(from, 1); its.splice(to, 0, m);
          return { ...sc, items: its };
        }),
      };
    }));
    setItemIdx(to);
  }, [subcatIdx]); // eslint-disable-line

  // ── 아이템 CRUD ──────────────────────────────────────────────────────────
  const addItem = (gi, si) => {
    const next = ref.current.map((g, i) => i === gi ? { ...g, subcats: g.subcats.map((sc, j) =>
      j === si ? { ...sc, items: [...sc.items, newItem()] } : sc) } : g);
    push(next); setItemIdx(next[gi].subcats[si].items.length - 1);
  };
  const delItem = (gi, si, ii) => {
    const it = ref.current[gi]?.subcats[si]?.items[ii];
    if (it?.intNum) delItems.current.push(it.intNum);
    push(ref.current.map((g, i) => i === gi ? { ...g, subcats: g.subcats.map((sc, j) =>
      j === si ? { ...sc, items: sc.items.filter((_, k) => k !== ii) } : sc) } : g));
    setItemIdx(idx => Math.max(0, Math.min(idx, (ref.current[gi]?.subcats[si]?.items?.length ?? 1) - 1)));
  };
  const updItem = (gi, si, ii, field, val) => push(ref.current.map((g, i) => i === gi ? { ...g, subcats: g.subcats.map((sc, j) =>
    j === si ? { ...sc, items: sc.items.map((it, k) => k === ii ? { ...it, [field]: val } : it) } : sc) } : g));

  // ── 이미지 ───────────────────────────────────────────────────────────────
  const setImageKey = (gi, si, ii, imgIdx, key) => push(ref.current.map((g, i) => i === gi ? { ...g, subcats: g.subcats.map((sc, j) =>
    j === si ? { ...sc, items: sc.items.map((it, k) => { if (k !== ii) return it; const keys = [...it.imageKeys]; keys[imgIdx] = key; return { ...it, imageKeys: keys }; }) } : sc) } : g));
  const addImageSlot = (gi, si, ii) => push(ref.current.map((g, i) => i === gi ? { ...g, subcats: g.subcats.map((sc, j) =>
    j === si ? { ...sc, items: sc.items.map((it, k) => k === ii ? { ...it, imageKeys: [...it.imageKeys, ''] } : it) } : sc) } : g));
  const delImageSlot = (gi, si, ii, imgIdx) => push(ref.current.map((g, i) => i === gi ? { ...g, subcats: g.subcats.map((sc, j) =>
    j === si ? { ...sc, items: sc.items.map((it, k) => k === ii ? { ...it, imageKeys: it.imageKeys.filter((_, p) => p !== imgIdx) } : it) } : sc) } : g));

  // ── 링크 ─────────────────────────────────────────────────────────────────
  const addLink = (gi, si, ii) => push(ref.current.map((g, i) => i === gi ? { ...g, subcats: g.subcats.map((sc, j) =>
    j === si ? { ...sc, items: sc.items.map((it, k) => k === ii ? { ...it, links: [...it.links, newLink()] } : it) } : sc) } : g));
  const delLink = (gi, si, ii, li) => push(ref.current.map((g, i) => i === gi ? { ...g, subcats: g.subcats.map((sc, j) =>
    j === si ? { ...sc, items: sc.items.map((it, k) => k === ii ? { ...it, links: it.links.filter((_, p) => p !== li) } : it) } : sc) } : g));
  const updLink = (gi, si, ii, li, field, val) => push(ref.current.map((g, i) => i === gi ? { ...g, subcats: g.subcats.map((sc, j) =>
    j === si ? { ...sc, items: sc.items.map((it, k) => k === ii ? { ...it, links: it.links.map((l, p) => p === li ? { ...l, [field]: val } : l) } : it) } : sc) } : g));

  // ── 저장 ─────────────────────────────────────────────────────────────────
  const save = useCallback(async () => {
    // 1) 삭제 예약된 그룹
    for (const id of delGroups.current) {
      const r = await authFetch(`${API_GROUP}/${id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error(`그룹 삭제 실패 (${r.status})`);
    }
    delGroups.current = [];

    // 2) 삭제 예약된 아이템
    for (const id of delItems.current) {
      const r = await authFetch(`${API_ITEM}/${id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error(`아이템 삭제 실패 (${r.status})`);
    }
    delItems.current = [];

    // 3) 그룹 + 아이템 저장
    for (let gi = 0; gi < ref.current.length; gi++) {
      const g = ref.current[gi];

      // 그룹 저장
      const gr = await authFetch(API_GROUP, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ intNum: g.intNum || undefined, strSolSlug: slug, strCatKey: g.category, sort: gi }),
      });
      if (!gr.ok) throw new Error(`그룹 저장 실패 (${gr.status})`);
      const grSaved = await gr.json();
      if (!g.intNum) {
        ref.current = ref.current.map(x => x === g ? { ...x, intNum: grSaved.intNum } : x);
        g.intNum = grSaved.intNum;
      }

      // 아이템 저장
      let itemSort = 0;
      for (const sc of g.subcats) {
        for (const it of sc.items) {
          const ir = await authFetch(API_ITEM, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              intNum: it.intNum || undefined,
              intGroupNum: g.intNum,
              strSubcat: sc.subcat,
              strSlug: it.intNum ? undefined : `${slug}-${toSlug(it.model)}-${Date.now()}`,
              strModel: it.model,
              sort: itemSort++,
              strDesc: '',
              textSpec: it.spec || '',
              imageKeys: it.imageKeys.filter(Boolean),
              features: it.features.split('\n').map(s => s.trim()).filter(Boolean),
              tags: it.tags,
            }),
          });
          if (!ir.ok) throw new Error(`아이템 저장 실패 (${ir.status})`);
          const irSaved = await ir.json();
          if (!it.intNum) {
            it.intNum = irSaved.intNum;
          }
        }
      }
    }
    if (domainSave) await domainSave();
    dirtyRef.current = false;
  }, [slug, domainSave]); // eslint-disable-line

  useEffect(() => {
    if (onRegisterSave) onRegisterSave(save);
    return () => { if (onRegisterSave) onRegisterSave(null); };
  }, [save, onRegisterSave]);

  const isDirty = useCallback(() => dirtyRef.current || !!(domainDirty && domainDirty()), [domainDirty]);
  useEffect(() => {
    if (onRegisterDirty) onRegisterDirty(isDirty);
    return () => { if (onRegisterDirty) onRegisterDirty(null); };
  }, [isDirty, onRegisterDirty]);

  // ── 드래그 정렬 훅 ────────────────────────────────────────────────────────
  const { containerRef: groupTabsRef, dragProps: groupDragProps, dropProps: groupDropProps, cardClass: groupCardClass } = useDragReorder(reorderGroup);
  const { containerRef: subcatTabsRef, dragProps: subcatDragProps, dropProps: subcatDropProps, cardClass: subcatCardClass } = useDragReorder(reorderSubcat);
  const { containerRef: itemBarRef, dragProps: itemDragProps, dropProps: itemDropProps, cardClass: itemCardClass } = useDragReorder(reorderItem);

  if (groups === null) return <div className="rp-admin-wrap"><p style={{ color: '#888' }}>불러오는 중…</p></div>;

  const activeGroup  = groups[groupIdx];
  const subcats      = activeGroup?.subcats ?? [];
  const activeSubcat = subcats[subcatIdx];
  const items        = activeSubcat?.items ?? [];
  const it           = items[itemIdx];

  return (
    <div className="rp-admin-wrap">
      <h2 className="rp-admin-title">{title}</h2>

      {/* 도메인 헤더 (옵션) */}
      {domainSection}

      {/* ── 대분류 탭 ── */}
      <div className="rp-admin-tabrow">
        <ul className="rp-admin-tabs" ref={groupTabsRef}>
          {groups.map((g, gi) => (
            <li key={g._id} data-flip={g._id}
              className={`${gi === groupIdx ? 'active' : ''} ${groupCardClass(gi)}`}
              {...groupDragProps(gi)} {...groupDropProps(gi)}>
              <span className="rp-admin-tab-handle">⠿</span>
              <input className="rp-admin-tab-inp" value={g.category} placeholder="대분류명"
                onClick={() => setGroupIdx(gi)} onChange={e => updGroup(gi, e.target.value)} />
              <button type="button" className="rp-admin-tab-del" onClick={() => delGroup(gi)} title="대분류 삭제">×</button>
            </li>
          ))}
        </ul>
        <button type="button" className="rp-admin-add-tab" onClick={addGroup}>+ 대분류</button>
      </div>

      {!activeGroup ? (
        <div className="rp-admin-empty-state"><p>대분류를 먼저 추가하세요.</p></div>
      ) : (
        <>
          {/* ── 소분류 탭 ── */}
          <div className="rp-admin-subcatrow">
            <ul className="rp-admin-subcattabs" ref={subcatTabsRef}>
              {subcats.map((sc, si) => (
                <li key={sc._id} data-flip={sc._id}
                  className={`${si === subcatIdx ? 'active' : ''} ${subcatCardClass(si)}`}
                  {...subcatDragProps(si)} {...subcatDropProps(si)}>
                  <span className="rp-admin-tab-handle" style={{ fontSize: '0.8rem', padding: '6px 3px 6px 6px' }}>⠿</span>
                  <input className="rp-admin-subcattab-inp" value={sc.subcat} placeholder="소분류명"
                    onClick={() => setSubcatIdx(si)} onChange={e => updSubcat(groupIdx, si, e.target.value)} />
                  <button type="button" className="rp-admin-tab-del" onClick={() => delSubcat(groupIdx, si)}>×</button>
                </li>
              ))}
            </ul>
            <button type="button" className="rp-admin-add-subcattab" onClick={() => addSubcat(groupIdx)}>+ 소분류</button>
          </div>

          {!activeSubcat ? (
            <div className="rp-admin-empty-state"><p>소분류를 먼저 추가하세요.</p></div>
          ) : (
            <>
              {/* ── 제품 탭 바 ── */}
              <div className="rp-admin-item-bar" ref={itemBarRef}>
                {items.map((item, i) => (
                  <div key={item._id} data-flip={item._id}
                    className={`rp-admin-item-tab ${i === itemIdx ? 'active' : ''} ${itemCardClass(i)}`}
                    {...itemDragProps(i)} {...itemDropProps(i)}>
                    <span className="rp-admin-tab-handle">⠿</span>
                    <button type="button" onClick={() => setItemIdx(i)}>
                      {item.model || `제품 ${i + 1}`}
                    </button>
                  </div>
                ))}
                <button type="button" className="rp-admin-item-add" onClick={() => addItem(groupIdx, subcatIdx)}>+ 제품 추가</button>
              </div>

              {/* ── 제품 에디터 ── */}
              {it ? (
                <div className="rp-admin-editor">
                  <div className="rp-admin-top">
                    {/* 이미지 갤러리 */}
                    <div className="rp-admin-gallery">
                      <div className="rp-admin-gallery-label">
                        이미지 갤러리 ({it.imageKeys.length}/6)
                        {it.imageKeys.length < 6 && (
                          <button type="button" className="rp-admin-gallery-add" onClick={() => addImageSlot(groupIdx, subcatIdx, itemIdx)}>+ 추가</button>
                        )}
                      </div>
                      <div className="rp-admin-gallery-grid">
                        {it.imageKeys.map((key, imgIdx) => (
                          <div key={key || `slot-${imgIdx}`} className="rp-admin-gallery-slot">
                            <button type="button" className="rp-admin-gallery-del" onClick={() => delImageSlot(groupIdx, subcatIdx, itemIdx, imgIdx)}>×</button>
                            <ImageDrop value={key} onChange={k => setImageKey(groupIdx, subcatIdx, itemIdx, imgIdx, k)} />
                          </div>
                        ))}
                        {it.imageKeys.length === 0 && (
                          <div className="rp-admin-gallery-empty" onClick={() => addImageSlot(groupIdx, subcatIdx, itemIdx)}>
                            <span>+ 이미지 추가</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* 기본정보 */}
                    <div className="rp-admin-info">
                      <button type="button" className="rp-admin-item-del" onClick={() => delItem(groupIdx, subcatIdx, itemIdx)}>제품 삭제 ×</button>

                      <label className="rp-admin-field-label">모델명</label>
                      <input className="rp-admin-field-inp" value={it.model} placeholder="모델명"
                        onChange={e => updItem(groupIdx, subcatIdx, itemIdx, 'model', e.target.value)} />

                      <label className="rp-admin-field-label">주요 특징 (한 줄에 하나씩)</label>
                      <textarea className="rp-admin-profile-inp" value={it.features} rows={5}
                        placeholder={"Intel® Core™ i7 Processor\nFanless Design\n-40°C ~ 85°C"}
                        onChange={e => updItem(groupIdx, subcatIdx, itemIdx, 'features', e.target.value)} />

                      <label className="rp-admin-field-label" style={{ marginTop: 8 }}>
                        태그 <span style={{ fontSize: 11, fontWeight: 400, color: '#888' }}>(검색·필터에 사용)</span>
                      </label>
                      <TagsEditor
                        tags={it.tags}
                        onChange={tags => updItem(groupIdx, subcatIdx, itemIdx, 'tags', tags)}
                      />

                      <label className="rp-admin-field-label" style={{ marginTop: 10 }}>링크 버튼 (최대 3개)</label>
                      {it.links.map((lnk, li) => (
                        <div key={lnk._id} className="rp-admin-link-row">
                          <input className="rp-admin-link-key" value={lnk.key} placeholder="Datasheet"
                            onChange={e => updLink(groupIdx, subcatIdx, itemIdx, li, 'key', e.target.value)} />
                          <input className="rp-admin-link-url" value={lnk.url} placeholder="https://..."
                            onChange={e => updLink(groupIdx, subcatIdx, itemIdx, li, 'url', e.target.value)} />
                          <button type="button" className="rp-admin-spec-del" onClick={() => delLink(groupIdx, subcatIdx, itemIdx, li)}>×</button>
                        </div>
                      ))}
                      {it.links.length < 3 && (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button type="button" className="rp-admin-add-btn" onClick={() => addLink(groupIdx, subcatIdx, itemIdx)}>+ 링크 추가</button>
                          <button type="button" className="rp-admin-add-btn" disabled={fileUploading}
                            onClick={() => {
                              filePending.current = (url) => {
                                const gi = groupIdxRef.current;
                                push(ref.current.map((g, gi2) => gi2 !== gi ? g : { ...g, subcats: g.subcats.map((sc, j) =>
                                  j !== subcatIdx ? sc : { ...sc, items: sc.items.map((it2, k) =>
                                    k !== itemIdx ? it2 : { ...it2, links: [...it2.links, { _id: uid(), key: 'Information', url }] }
                                  )}
                                )}));
                              };
                              filePick.current?.click();
                            }}>
                            {fileUploading ? '업로드 중…' : '+ 파일 추가'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 스펙 */}
                  <div className="rp-admin-section" style={{ marginTop: 16 }}>
                    <label className="rp-admin-field-label">스펙 (텍스트)</label>
                    <SectionBuilder
                      key={it._id + '-spec'}
                      value={it.spec}
                      onChange={v => updItem(groupIdx, subcatIdx, itemIdx, 'spec', v)}
                    />
                  </div>
                </div>
              ) : (
                <div className="rp-admin-empty-state">
                  <p>이 소분류에 제품이 없습니다.</p>
                  <button type="button" className="rp-admin-add-btn" onClick={() => addItem(groupIdx, subcatIdx)}>+ 제품 추가</button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {onBack && (
        <div style={{ marginTop: '1.5rem' }}>
          <button type="button" className="btn-secondary" onClick={onBack}>← 응용 분야로 돌아가기</button>
        </div>
      )}

      <input ref={filePick} type="file" accept=".pdf,.xlsx,.xls,.docx,.doc,.zip" style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; if (f) uploadFile(f); e.target.value = ''; }} />
    </div>
  );
}
